import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { getBlogPostBySlug } from '../data/blogData';
import SEOHead from './SEOHead';
import ReactMarkdown from 'react-markdown';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Artigo não encontrado</h1>
          <Button onClick={() => navigate('/blog')}>Voltar para o Blog</Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('pt-BR', options);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={`${post.title} | Golden Pax Rio`}
        description={post.excerpt}
        keywords={`${post.title}, serviços funerários rj, golden pax rio`}
        canonical={`https://pensive-jemison-1.preview.emergentagent.com/blog/${post.slug}`}
        ogImage={post.image}
      />

      {/* Hero Image */}
      <div className="w-full h-96 overflow-hidden relative">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover"
          loading="eager"
          decoding="async"
          width="1200"
          height="600"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
      </div>

      {/* Article Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate('/blog')}
            className="mb-6 hover:bg-emerald-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para o Blog
          </Button>

          {/* Article Header */}
          <article className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            {/* Meta info */}
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post.date)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{post.readTime} de leitura</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-gray-600 mb-8 pb-8 border-b">
              {post.excerpt}
            </p>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 space-y-2" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-4 space-y-2" {...props} />,
                  li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                  table: ({node, ...props}) => <div className="overflow-x-auto my-6"><table className="min-w-full border-collapse border border-gray-300" {...props} /></div>,
                  th: ({node, ...props}) => <th className="border border-gray-300 bg-emerald-50 px-4 py-2 text-left font-bold" {...props} />,
                  td: ({node, ...props}) => <td className="border border-gray-300 px-4 py-2" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-[#2E6B35] pl-4 italic my-4 text-gray-600" {...props} />,
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          </article>

          {/* CTA Box */}
          <div className="bg-[#2E6B35] text-white rounded-lg p-8 mt-8">
            <h3 className="text-2xl font-bold mb-4">Precisa de Ajuda?</h3>
            <p className="text-lg mb-6 opacity-90">
              Nossa equipe da Golden Pax Rio está disponível 24 horas para atender sua família com dignidade e respeito.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-white text-[#2E6B35] hover:bg-gray-100"
                onClick={() => window.open('tel:972300064', '_blank')}
              >
                <Phone className="w-5 h-5 mr-2" />
                (021) 97230-0064
              </Button>
              <Button 
                size="lg" 
                className="bg-white text-[#2E6B35] hover:bg-gray-100"
                onClick={() => window.open('https://wa.me/5521972300064?text=Ol%C3%A1!%20Encontrei%20voc%C3%AAs%20pelo%20site%20e%20gostaria%20de%20obter%20mais%20informa%C3%A7%C3%B5es%2C%20por%20favor.', '_blank')}
              >
                WhatsApp
              </Button>
            </div>
          </div>

          {/* Back to Blog */}
          <div className="text-center mt-8">
            <Button 
              onClick={() => navigate('/blog')}
              className="bg-[#2E6B35] hover:bg-[#2E6B35] text-white"
            >
              Ver Mais Artigos
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;